import { isValidCountryCode } from '@tulasihome/shared';

export interface ShippingAddress {
  readonly countryCode: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly phone: string;
  readonly street1: string;
  readonly street2?: string;
  readonly city: string;
  readonly novaposhtaCityRef?: string;
  readonly region: string;
  readonly postalCode: string;
  readonly setAsDefault: boolean;
}

export type ShippingAddressValidationErrors = Partial<Record<
  'countryCode' | 'firstName' | 'lastName' | 'phone' | 'street1' | 'city' | 'region' | 'postalCode',
  string
>>;

export type ShippingAddressValidationMessages = {
  readonly countryCode: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly phone: string;
  readonly street1: string;
  readonly poBoxStreet: string;
  readonly city: string;
  readonly novaposhtaCitySelection: string;
  readonly region: string;
  readonly postalCode: string;
};

const required = (value: string | undefined, minLength: number): boolean => (value?.trim().length ?? 0) >= minLength;

const poBoxPattern = /\b(?:p\.?\s*o\.?\s*box|post\s+office\s+box)\b/i;

const containsPoBox = (value: string | undefined): boolean => poBoxPattern.test(value ?? '');

const containsStreetAndHouseNumber = (value: string | undefined): boolean => {
  const normalized = value?.trim() ?? '';
  if (!normalized || containsPoBox(normalized)) {
    return false;
  }

  return /\d/.test(normalized) && normalized.length >= 5;
};

export const validateShippingAddress = (
  address: Pick<
    ShippingAddress,
    'countryCode' | 'firstName' | 'lastName' | 'phone' | 'street1' | 'street2' | 'city' | 'novaposhtaCityRef' | 'region' | 'postalCode'
  >,
  messages: ShippingAddressValidationMessages,
  options?: {
    readonly requiresNovaposhtaCitySelection?: boolean;
  },
): ShippingAddressValidationErrors => {
  const errors: ShippingAddressValidationErrors = {};

  if (!isValidCountryCode(address.countryCode)) {
    errors.countryCode = messages.countryCode;
  }
  if (!required(address.firstName, 1)) {
    errors.firstName = messages.firstName;
  }
  if (!required(address.lastName, 1)) {
    errors.lastName = messages.lastName;
  }
  if (!required(address.phone, 7)) {
    errors.phone = messages.phone;
  }
  if (!required(address.street1, 5)) {
    errors.street1 = messages.street1;
  }
  if (
    (containsPoBox(address.street1) || containsPoBox(address.street2))
    && !containsStreetAndHouseNumber(address.street1)
    && !containsStreetAndHouseNumber(address.street2)
  ) {
    errors.street1 = messages.poBoxStreet;
  }
  if (!required(address.city, 2)) {
    errors.city = messages.city;
  }
  if (options?.requiresNovaposhtaCitySelection && !required(address.novaposhtaCityRef, 1)) {
    errors.city = messages.novaposhtaCitySelection;
  }
  if (!required(address.region, 2)) {
    errors.region = messages.region;
  }
  if (!required(address.postalCode, 3)) {
    errors.postalCode = messages.postalCode;
  }

  return errors;
};
